// Shared geometry for a fanned row of cards (the viewer's own HoleCards and opponents'
// SeatCards both use this). Keeping the overlap/rotation math in ONE place guarantees the
// resting layout and the deal-animation fly-in agree — otherwise cards visibly jump when the
// deal animation ends.
//
// Design goals (from the redesign):
//   * The viewer's own 2-card hand keeps its dedicated CSS (.hole-cards .hand-card :first/:last/
//     :only-child), so HoleCards only consults this module for N > 2; opponents' SeatCards use it
//     for every N.
//   * Cards overlap MORE as the count grows, but never more than the 5-card reference — 4-card is
//     looser than 5; 6 and 8 clamp the horizontal overlap. Tilt is a fixed -4°..+4° fan for any
//     count (rotateDeg), independent of the overlap.

// The player-info bubble's inner content width (min-width 100 + no box-sizing reset). A 4+ card
// fan is centered on the bubble and constrained so its outermost cards' outer bottom corners
// (border included) stay within this width — see fitFan. Tunable; lower for a bigger inset.
export const FAN_FIT_WIDTH = 100;

// Fit an n-card fan within `targetWidth` so the outermost cards' outer bottom corners — BORDER
// included — stay inside it. The fan is centered on the bubble and tilt puts each outer card's
// bottom corner essentially at the row edge, so the constraint is the actual rendered footprint:
//   footprint = n*(cardWidth + 2*border) - (n-1)*overlap  <=  targetWidth
//   n <= 3 : unchanged (baseCardWidth, baseOverlap) — the fan already fits with room to spare.
//   n = 4,5: keep card size, raise the (uniform) overlap only as needed to fit (grows 4 -> 5).
//   n >= 6 : hold the 5-card overlap-to-footprint ratio and shrink the whole card (content,
//            border and fonts scale by `scale`) until it fits.
// Returns { cardWidth, overlap, scale }. cardWidth is the CONTENT width — pass it to fanLayout and
// use it for the inline card width; render the border at `border*scale`. The overlap is the
// marginLeft (in footprint px). scale === 1 means "render exactly as the CSS defaults" (no inline
// sizing), so every already-fitting count (n<=5 hero, 2-card, mini <=5) is byte-identical.
export function fitFan(n, baseCardWidth, cardBorder, targetWidth, baseOverlap) {
  const cw0 = baseCardWidth;
  if (n <= 3) return { cardWidth: cw0, overlap: baseOverlap, scale: 1 };

  const fp0 = cw0 + 2 * cardBorder;                     // base footprint (border-box) width
  const ovForFit = (m, fp) => (m * fp - targetWidth) / (m - 1); // overlap making footprint == target
  // 4/5-card overlap: at least the base overlap, more if needed to fit, never a near-total cover.
  const overlapAt = (m) => Math.min(cw0 - 6, Math.max(baseOverlap, ovForFit(m, fp0)));

  if (n <= 5) return { cardWidth: cw0, overlap: overlapAt(n), scale: 1 };

  // n >= 6: keep the 5-card overlap ratio (relative to footprint) and shrink uniformly to fit.
  const r = overlapAt(5) / fp0;
  const scale = Math.min(1, targetWidth / (fp0 * (n - (n - 1) * r)));
  return { cardWidth: cw0 * scale, overlap: r * fp0 * scale, scale };
}

// Horizontal overlap (px the next card slides over the previous), clamped to the 5-card max.
// 2→16, 3→18, 4→20, 5→22, 6/8→22. cardWidth scales it for the smaller opponent cards.
export function overlapPx(n, cardWidth = 48) {
  const refOverlap = 22;            // 5-card reference at the 48px hero width = the maximum
  const raw = 12 + 2 * n;
  const scaled = (Math.min(refOverlap, raw) * cardWidth) / 48;
  // Never overlap so hard that cards fully cover each other.
  return Math.min(scaled, cardWidth - 6);
}

// Per-card tilt (deg): the leftmost card is -MAX_TILT_DEG, the rightmost is +MAX_TILT_DEG, and the
// rest step linearly between. Odd counts leave one card flat (0°) in the middle; even counts
// straddle the centre with NO flat card — equivalent to taking the odd-length symmetric sequence
// and dropping its middle 0, so each half stays evenly stepped while the gap across the centre
// doubles. Examples:
//   1 → 0            2 → -4, 4              3 → -4, 0, 4          4 → -4, -2, 2, 4
//   5 → -4, -2, 0, 2, 4                     6 → -4, -2.67, -1.33, 1.33, 2.67, 4
const MAX_TILT_DEG = 4;
export function rotateDeg(i, n) {
  if (n <= 1) return 0;
  const k = Math.floor(n / 2);      // cards on each side of the centre
  const step = MAX_TILT_DEG / k;    // even step within each half
  // Virtual symmetric slots -k..+k; odd n keeps the 0 slot in the middle, even n skips it.
  const slot = n % 2 === 1 ? i - k : (i < k ? i - k : i - k + 1);
  return slot * step;
}

// Per-card left margin: 0 for the first card, negative overlap for the rest.
export function marginLeftPx(i, n, cardWidth = 48) {
  return i === 0 ? 0 : -overlapPx(n, cardWidth);
}

// Full layout for an N-card fan at a given card width. Returns, per card:
//   { rotate, marginLeft, naturalCenterX }  — where naturalCenterX is the card's center X
// measured from the left edge of the rendered row (used to compute the deal-animation startX).
// Also returns totalWidth and containerCenter for centering the fly-in.
//
// `opts.overlap` overrides the default per-count horizontal overlap — the hero's own PLO hand
// stacks tighter so only each card's top-left index strip peeks out (the corner-index look);
// opponents pass none and keep the looser default. Tilt is always the fixed -4°..+4° fan from
// rotateDeg, regardless of count.
export function fanLayout(n, cardWidth = 48, opts = {}) {
  const overlap = opts.overlap != null ? opts.overlap : overlapPx(n, cardWidth);
  const cards = [];
  let x = 0; // running left edge of the current card
  for (let i = 0; i < n; i++) {
    const ml = i === 0 ? 0 : -overlap; // first card flush; the rest slide over the previous
    x += ml; // margin shifts this card's left edge
    cards.push({
      rotate: rotateDeg(i, n),
      marginLeft: ml,
      naturalCenterX: x + cardWidth / 2,
    });
    x += cardWidth; // advance past this card
  }
  const totalWidth = x;
  return { cards, totalWidth, containerCenter: totalWidth / 2 };
}
